import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, Clock, AlertCircle, ArrowRight, Sparkles } from "lucide-react";
import SPShell from "@/components/SPShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import SPCockpit from "@/pages/cockpits/SPCockpit";

const STATUS_STYLE = {
  NEW: { bg: "var(--blue-light)", color: "var(--blue-dark)", label: "New" },
  VIEWED: { bg: "#fef3c7", color: "#92400e", label: "Viewed" },
  RESPONDED: { bg: "#dcfce7", color: "#166534", label: "Responded" },
  QUOTED: { bg: "#dcfce7", color: "#166534", label: "Quoted" },
  DECLINED: { bg: "#fee2e2", color: "#991b1b", label: "Declined" },
  CLOSED: { bg: "#e2e8f0", color: "#475569", label: "Closed" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.NEW;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
      data-testid={`sp-lead-status-${status}`}
    >
      {s.label}
    </span>
  );
}

export default function SPDashboard() {
  const [sp, setSp] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [me, ls] = await Promise.all([
          api.get("/sp/me"),
          api.get("/sp/leads"),
        ]);
        if (!mounted) return;
        setSp(me.data);
        setLeads(ls.data);
      } catch (e) {
        setError(apiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const counts = {
    total: leads.length,
    open: leads.filter((l) => ["NEW", "VIEWED"].includes(l.status)).length,
    responded: leads.filter((l) => ["RESPONDED", "QUOTED"].includes(l.status)).length,
    declined: leads.filter((l) => l.status === "DECLINED").length,
  };
  const recent = leads.slice(0, 5);

  return (
    <SPShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="sp-dashboard">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1
              className="text-2xl font-bold tracking-tight"
              style={{ color: "var(--navy)" }}
              data-testid="sp-dashboard-heading"
            >
              Welcome back{sp?.business_name ? `, ${sp.business_name}` : ""}
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
              Your verified strata leads, routed via Opstrait.
            </p>
          </div>
          {sp?.verified && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
              style={{ background: "#dcfce7", color: "#166534" }}
              data-testid="sp-verified-badge"
            >
              <CheckCircle2 size={13} strokeWidth={2.5} />
              Verified partner
            </span>
          )}
        </div>

        {error && (
          <div
            className="rounded-lg p-4 text-sm mb-6"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="sp-dashboard-error"
          >
            {error}
          </div>
        )}

        {/* Home Pages Rework — persona-specific cockpit (Broker vs generic SP) */}
        <div className="mb-8">
          <SPCockpit isBroker={sp?.provider_type === "INSURANCE_BROKER"} />
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {[
            { key: "open", label: "Open leads", value: counts.open, icon: Clock, accent: "var(--blue)" },
            { key: "responded", label: "Responded", value: counts.responded, icon: CheckCircle2, accent: "#16a34a" },
            { key: "declined", label: "Declined", value: counts.declined, icon: AlertCircle, accent: "#dc2626" },
            { key: "total", label: "All-time leads", value: counts.total, icon: Inbox, accent: "var(--navy)" },
          ].map((t) => (
            <div
              key={t.key}
              className="rounded-xl bg-white border p-4"
              style={{ borderColor: "var(--border)" }}
              data-testid={`sp-tile-${t.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  {t.label}
                </span>
                <t.icon size={16} style={{ color: t.accent }} />
              </div>
              <div className="text-3xl font-bold" style={{ color: "var(--navy)" }}>
                {loading ? <Skeleton className="h-8 w-12" /> : t.value}
              </div>
            </div>
          ))}
        </div>

        {/* Recent leads */}
        <div
          className="rounded-xl bg-white border"
          style={{ borderColor: "var(--border)" }}
          data-testid="sp-recent-leads"
        >
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} style={{ color: "var(--blue)" }} />
              <h2 className="text-sm font-semibold" style={{ color: "var(--navy)" }}>
                Recent leads
              </h2>
            </div>
            <Link to="/sp/leads">
              <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="sp-view-all-leads">
                View all
                <ArrowRight size={12} className="ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : recent.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="sp-no-leads">
              <Inbox size={28} className="mx-auto mb-3" style={{ color: "var(--slate)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--navy)" }}>No leads yet</p>
              <p className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                When committees request quotes routed to you, they will appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
              {recent.map((l) => (
                <li key={l.lead_id} data-testid={`sp-recent-lead-${l.lead_id}`}>
                  <Link
                    to={`/sp/leads/${l.lead_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate" style={{ color: "var(--navy)" }}>
                          {l.scheme_name || "Scheme"}
                        </span>
                        <StatusPill status={l.status} />
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--slate)" }}>
                        {l.source === "insurance_quote_request" ? "Insurance quote request" : l.source}
                        {" · "}
                        {new Date(l.submitted_at).toLocaleString()}
                      </div>
                    </div>
                    <ArrowRight size={14} style={{ color: "var(--slate)" }} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SPShell>
  );
}
